import { useEffect, useRef, useState } from "react";
import type { SceneHeadingFields, SceneHeadingKind } from "@supermanus/document-model";
import { formatSceneHeading, parseItemNumber } from "@supermanus/document-model";
import { handleFieldEnterKey } from "./fieldKeyboard";
import { placeCaretInInput } from "./caretFromPoint";
import { InlineEditable } from "./InlineEditable";

type EditMode = "location" | "time" | "note" | null;

export interface SceneHeadingLineProps {
  sceneNumber: string | number;
  headingKind?: SceneHeadingKind;
  headingLocation?: string;
  headingTime?: string;
  headingNote?: string;
  /** Cached joined display; used if parts are empty (legacy). */
  heading?: string;
  readOnly?: boolean;
  onChange: (patch: SceneHeadingFields) => void;
  /** When set, the scene number is click-to-edit. */
  onNumberChange?: (number: string) => void;
  className?: string;
}

function partsFromProps(props: SceneHeadingLineProps): SceneHeadingFields {
  return {
    headingKind: props.headingKind,
    headingLocation: props.headingLocation,
    headingTime: props.headingTime,
    headingNote: props.headingNote,
  };
}

/**
 * "Scene N" plus optional heading parts joined with " - ".
 * Empty by default; hover reveals INT / EXT / LOCATION / TIDSPUNKT / VALGFRI TEKST.
 */
export function SceneHeadingLine({
  sceneNumber,
  headingKind,
  headingLocation,
  headingTime,
  headingNote,
  heading,
  readOnly,
  onChange,
  onNumberChange,
  className,
}: SceneHeadingLineProps) {
  const [editMode, setEditMode] = useState<EditMode>(null);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const parts: SceneHeadingFields = {
    headingKind,
    headingLocation,
    headingTime,
    headingNote,
  };
  const display =
    formatSceneHeading(parts) || (heading ?? "").trim();

  useEffect(() => {
    if (!editMode) return;
    const el = inputRef.current;
    if (!el) return;
    placeCaretInInput(el, null, "end");
  }, [editMode]);

  function commit(next: SceneHeadingFields) {
    onChange({
      headingKind: next.headingKind,
      headingLocation: next.headingLocation?.trim() || undefined,
      headingTime: next.headingTime?.trim() || undefined,
      headingNote: next.headingNote?.trim() || undefined,
    });
  }

  function startEdit(mode: EditMode) {
    if (readOnly || !mode) return;
    if (mode === "location") setDraft(headingLocation ?? "");
    else if (mode === "time") setDraft(headingTime ?? "");
    else setDraft(headingNote ?? "");
    setEditMode(mode);
  }

  function commitEdit() {
    if (!editMode) return;
    const value = draft.trim();
    const base = partsFromProps({
      sceneNumber,
      headingKind,
      headingLocation,
      headingTime,
      headingNote,
      onChange,
    });
    if (editMode === "location") {
      commit({ ...base, headingLocation: value || undefined });
    } else if (editMode === "time") {
      commit({ ...base, headingTime: value || undefined });
    } else {
      commit({ ...base, headingNote: value || undefined });
    }
    setEditMode(null);
  }

  function setKind(kind: SceneHeadingKind) {
    if (readOnly) return;
    const base = partsFromProps({
      sceneNumber,
      headingKind,
      headingLocation,
      headingTime,
      headingNote,
      onChange,
    });
    // Toggle off if clicking the active kind again
    commit({
      ...base,
      headingKind: base.headingKind === kind ? undefined : kind,
    });
  }

  const editing = editMode !== null;

  return (
    <div
      className={`sm-script-scene-num-line${className ? ` ${className}` : ""}${
        display || editing ? " has-heading" : ""
      }`}
    >
      <span className="sm-scene-num">
        Scene{" "}
        {onNumberChange && !readOnly ? (
          <InlineEditable
            as="span"
            className="sm-scene-num-value"
            value={String(sceneNumber)}
            title="Klik for at ændre scenenummer"
            onChange={(raw) => {
              const parsed = parseItemNumber(raw);
              if (parsed && parsed !== String(sceneNumber)) {
                onNumberChange(parsed);
              }
            }}
          />
        ) : (
          sceneNumber
        )}
      </span>

      {editing ? (
        <input
          ref={inputRef}
          className="sm-inline-input sm-scene-heading-input sm-inline-fill"
          value={draft}
          size={1}
          placeholder={
            editMode === "location"
              ? "Sted / location"
              : editMode === "time"
                ? "Tidspunkt (fx DAG, AFTEN)"
                : "Valgfri tekst"
          }
          aria-label={
            editMode === "location"
              ? "Location"
              : editMode === "time"
                ? "Tidspunkt"
                : "Valgfri tekst"
          }
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitEdit}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            handleFieldEnterKey(e);
            if (e.key === "Escape") {
              e.preventDefault();
              setEditMode(null);
            }
            e.stopPropagation();
          }}
        />
      ) : display || !readOnly ? (
        <span
          className={`sm-scene-heading sm-scene-heading-text${
            display ? "" : " is-empty"
          }${readOnly ? "" : " is-editable"}`}
          title={readOnly ? undefined : "Klik for at redigere location"}
          onClick={
            readOnly
              ? undefined
              : (e) => {
                  e.stopPropagation();
                  startEdit("location");
                }
          }
        >
          {display}
        </span>
      ) : null}

      {!readOnly && !editing ? (
        <div
          className="sm-scene-heading-actions"
          role="toolbar"
          aria-label="Sceneoverskrift"
        >
          <button
            type="button"
            className={headingKind === "int" ? "is-active" : undefined}
            onClick={(e) => {
              e.stopPropagation();
              setKind("int");
            }}
          >
            INT
          </button>
          <button
            type="button"
            className={headingKind === "ext" ? "is-active" : undefined}
            onClick={(e) => {
              e.stopPropagation();
              setKind("ext");
            }}
          >
            EXT
          </button>
          <button
            type="button"
            className={headingLocation?.trim() ? "is-active" : undefined}
            onClick={(e) => {
              e.stopPropagation();
              startEdit("location");
            }}
          >
            LOCATION
          </button>
          <button
            type="button"
            className={headingTime?.trim() ? "is-active" : undefined}
            onClick={(e) => {
              e.stopPropagation();
              startEdit("time");
            }}
          >
            TIDSPUNKT
          </button>
          <button
            type="button"
            className={headingNote?.trim() ? "is-active" : undefined}
            onClick={(e) => {
              e.stopPropagation();
              startEdit("note");
            }}
          >
            VALGFRI TEKST
          </button>
        </div>
      ) : null}
    </div>
  );
}
